import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DataTable } from 'shared/components/DataTable/DataTable'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import Modal from 'shared/ui/Modal/Modal'
import { ListsTabs } from 'widgets/ListsTabs/ListsTabs'
import { navigateToStudentProfile } from 'pages/Dashboard/components/TodaysPrioritySection/utils/priorityCardUi'
import { ReactComponent as ListIcon } from 'assets/icons/list.svg'
import { ReactComponent as ChevronIcon } from 'assets/icons/arrow_short_right.svg'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as PersonAddIcon } from 'assets/icons/person_add.svg'
import { ReactComponent as TrashIcon } from 'assets/icons/trash-2.svg'
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg'
import { ReactComponent as StarIcon } from 'assets/icons/check_star.svg'
import { CONTACT_TYPE_LABELS } from '../../data/parentNotificationData'
import ImportContactsModal from './ImportContactsModal'
import InlineErrorBanner from '../InlineErrorBanner/InlineErrorBanner'
import useContacts from './hooks/useContacts'
import s from './Contacts.module.scss'

const CONTACTS_SUMMARY_LIMIT = 2

const formatContactsSummary = contacts => {
	const list = Array.isArray(contacts) ? contacts : []
	const names = list
		.map(contact => String(contact.contact_name || '').trim() || contact.email || '')
		.filter(Boolean)

	if (!names.length) {
		return { names: [], overflow: 0 }
	}

	if (names.length <= CONTACTS_SUMMARY_LIMIT) {
		return { names, overflow: 0 }
	}

	return {
		names: names.slice(0, CONTACTS_SUMMARY_LIMIT),
		overflow: names.length - CONTACTS_SUMMARY_LIMIT,
	}
}

const ContactTypePill = ({ type }) => (
	<span className={s.typePill}>{CONTACT_TYPE_LABELS[type] || type || '—'}</span>
)

const ExpandedContacts = ({
	row,
	openEditContact,
	requestDelete,
}) => {
	const contacts = row.contacts || []

	return (
		<div className={s.expanded}>
			<div className={s.expanded__panel}>
				<div className={s.expanded__title}>Contacts ({contacts.length})</div>
				{contacts.length ? (
					<div className={s.expanded__tableWrap}>
						<table className={s.expanded__table}>
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th>Type</th>
									<th>Primary</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{contacts.map(contact => (
									<tr key={contact.id}>
										<td>
											<span className={s.expanded__name}>
												{contact.contact_name || '—'}
											</span>
										</td>
										<td>
											<span className={s.expanded__email}>
												{contact.email || '—'}
											</span>
										</td>
										<td>
											<ContactTypePill type={contact.contact_type} />
										</td>
										<td>
											{contact.is_primary ? (
												<span className={s.primaryPill}>
													<span className={s.icon} aria-hidden="true">
														<StarIcon />
													</span>
													Primary
												</span>
											) : (
												<span className={s.primaryEmpty}>—</span>
											)}
										</td>
										<td>
											<div className={s.iconActions}>
												<button
													type="button"
													className={s.iconEdit}
													aria-label={`Edit ${contact.contact_name || 'contact'}`}
													onClick={() =>
														openEditContact(row, { contactId: contact.id })
													}
												>
													<span
														className={`${s.icon} ${s.editIcon}`}
														aria-hidden="true"
													>
														<PenIcon />
													</span>
												</button>
												<button
													type="button"
													className={s.iconDelete}
													aria-label={`Delete ${contact.contact_name || 'contact'}`}
													onClick={() => requestDelete(row, contact)}
												>
													<span className={s.icon} aria-hidden="true">
														<TrashIcon />
													</span>
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<p className={s.expanded__empty}>No contacts for this student yet.</p>
				)}
			</div>
		</div>
	)
}

export const Contacts = ({
	contactRows,
	openAddContact,
	openEditContact,
	deleteContact,
	contactSaving,
	contactsLoadStatus,
	contactsListError,
	loadContacts,
	dismissContactsListError,
	onImportComplete,
}) => {
	const navigate = useNavigate()
	const [isImportOpen, setIsImportOpen] = useState(false)
	const [deleteConfirm, setDeleteConfirm] = useState(null)
	const isRetrying = contactsLoadStatus === 'loading'
	const rows = contactRows || []

	const {
		visibleRows,
		tabs,
		activeGrade,
		setActiveGrade,
		sortKey,
		sortDir,
		setSort,
		studentSortOptions,
		expandedIds,
		toggleExpand,
		visibleCount,
	} = useContacts({ contactRows: rows })

	const openStudentProfile = useCallback(
		(event, studentId) => {
			event.stopPropagation()
			navigateToStudentProfile(navigate, studentId)
		},
		[navigate],
	)

	const requestDelete = (row, contact) => {
		setDeleteConfirm({
			studentId: row.studentId,
			studentName: row.studentName,
			contactId: contact.id,
			contactName: contact.contact_name || contact.email || 'this parent',
		})
	}

	const cancelDelete = () => {
		if (contactSaving) return
		setDeleteConfirm(null)
	}

	const confirmDelete = async () => {
		if (!deleteConfirm || contactSaving) return
		await deleteContact(deleteConfirm.contactId, {
			studentId: deleteConfirm.studentId,
			keepModalOpen: false,
		})
		setDeleteConfirm(null)
	}

	const columns = useMemo(
		() => [
			{
				id: 'student',
				label: 'Student',
				sortable: true,
				isSortActive: sortKey === 'student',
				sortDir,
				sortOptions: studentSortOptions,
				onSortSelect: opt => setSort(opt.key, opt.dir),
				headerClassName: s.colStudentHeader,
				cellClassName: s.colStudent,
				render: row => (
					<button
						type="button"
						className={s.student}
						onClick={event => openStudentProfile(event, row.studentId)}
						aria-label={`Open ${row.studentName || 'student'} profile`}
					>
						<InitialAvatar
							name={row.studentName}
							avatarKey={row.avatarKey}
							size={34}
							studentId={row.studentId}
						/>
						<span className={s.student__text}>
							<strong>{row.studentName}</strong>
							{row.grade ? <small>{row.grade}</small> : null}
						</span>
					</button>
				),
			},
			{
				id: 'contacts',
				label: 'Contacts',
				headerClassName: s.colContactsHeader,
				cellClassName: s.colContacts,
				render: row => {
					const summary = formatContactsSummary(row.contacts)
					return (
						<span className={s.contactsSummary}>
							{summary.names.length ? (
								<>
									{summary.names.map(name => (
										<span key={name} className={s.contactsName}>
											{name}
										</span>
									))}
									{summary.overflow > 0 ? (
										<span className={s.contactsOverflow}>
											+{summary.overflow}
										</span>
									) : null}
								</>
							) : (
								<span className={s.contactsEmpty}>—</span>
							)}
						</span>
					)
				},
			},
			{
				id: 'actions',
				label: 'Actions',
				headerClassName: s.colActionsHeader,
				cellClassName: s.colActions,
				render: row => {
					const hasContacts = Boolean(row.contacts?.length)
					return (
						<div
							className={s.actions}
							onClick={event => event.stopPropagation()}
						>
							<button
								type="button"
								className={s.editBtn}
								disabled={!hasContacts}
								onClick={() => openEditContact(row)}
							>
								<span className={`${s.icon} ${s.editIcon}`} aria-hidden="true">
									<PenIcon />
								</span>
								Edit
							</button>
							<button
								type="button"
								className={s.addBtn}
								onClick={() => openAddContact(row)}
							>
								<span className={s.icon} aria-hidden="true">
									<PersonAddIcon />
								</span>
								Add contact
							</button>
						</div>
					)
				},
			},
			{
				id: 'expand',
				label: '',
				width: '52px',
				headerClassName: s.colExpandHeader,
				cellClassName: s.colExpand,
				render: (row, { isExpanded }) => (
					<span
						className={`${s.expandIcon} ${isExpanded ? s.expandIconOpen : ''}`.trim()}
						aria-hidden
					>
						<ChevronIcon />
					</span>
				),
			},
		],
		[
			openAddContact,
			openEditContact,
			openStudentProfile,
			setSort,
			sortDir,
			sortKey,
			studentSortOptions,
		],
	)

	const emptyText =
		activeGrade && activeGrade !== 'all'
			? 'No students match these filters'
			: 'No students found'

	return (
		<>
			<div className={s.wrap} aria-labelledby="parent-contacts-title">
				<div className={s.toolbar}>
					<div className={s.header}>
						<div className={s.headerCopy}>
							<h2 id="parent-contacts-title" className={s.title}>
								Parent contacts
							</h2>
							<p className={s.description}>
								Manage parent and guardian contacts that receive enabled updates.
							</p>
						</div>
						<div className={s.headerActions}>
							<span className={s.countPill}>{visibleCount} students</span>
							<button
								type="button"
								className={s.importBtn}
								onClick={() => setIsImportOpen(true)}
							>
								<span className={s.icon} aria-hidden="true">
									<UploadIcon />
								</span>
								Import contacts
							</button>
						</div>
					</div>

					{contactsLoadStatus === 'loading' && !contactsListError ? (
						<p className={s.description}>Loading contacts…</p>
					) : null}

					{contactsListError ? (
						<InlineErrorBanner
							message={contactsListError}
							onClose={dismissContactsListError}
							onRetry={loadContacts}
							retrying={isRetrying}
						/>
					) : null}
				</div>

				<div className={s.tableArea}>
					<div className={s.tabs}>
						<ListsTabs
							className={s.listsTabs}
							badgeIcon={ListIcon}
							badgeText="Lists:"
							selectPrefixIcon={ListIcon}
							selectPrefixText="Lists:"
							tabs={tabs}
							activeTab={activeGrade}
							onSelect={setActiveGrade}
						/>
					</div>
					<DataTable
						variant="light"
						className={s.table}
						columns={columns}
						rows={visibleRows}
						getRowId={row => row.studentId}
						emptyText={emptyText}
						expandedRowIds={expandedIds}
						onToggleExpand={toggleExpand}
						onRowClick={row => toggleExpand(row.studentId)}
						renderExpandedRow={row => (
							<ExpandedContacts
								row={row}
								openEditContact={openEditContact}
								requestDelete={requestDelete}
							/>
						)}
						minWidth={820}
					/>
				</div>
			</div>

			{isImportOpen ? (
				<ImportContactsModal
					onClose={() => setIsImportOpen(false)}
					onImportComplete={onImportComplete}
				/>
			) : null}

			<Modal
				isOpen={Boolean(deleteConfirm)}
				onClose={cancelDelete}
				title="Delete parent contact?"
				subtitle={
					deleteConfirm
						? `Are you sure you want to delete ${deleteConfirm.contactName} from ${deleteConfirm.studentName}?`
						: ''
				}
				ariaLabel="Delete parent contact confirmation"
				width={480}
				compact
				closeOnBackdrop={!contactSaving}
				closeOnEsc={!contactSaving}
				footerButtons={[
					{
						id: 'cancel',
						text: 'Cancel',
						variant: 'secondary',
						disabled: contactSaving,
						onClick: cancelDelete,
					},
					{
						id: 'delete',
						text: contactSaving ? 'Deleting…' : 'Delete contact',
						variant: 'primary',
						disabled: contactSaving,
						onClick: confirmDelete,
					},
				]}
			/>
		</>
	)
}

export default Contacts
